import {
    type FilteredItemsSingleItem,
    useFilteredItems,
    useGetCurrencyConversion,
    useGroupFilterCount,
    useGroupFilterParams,
    useGroupId,
} from '@pkg/hooks';

import { Currency, type CurrencyConversionRate } from '@app/client/common/structs/nft/currencies';
import { GET_GROUP_ITEMS_LIMIT } from '@app/client/resources/groups';

interface OnlyAcceptedConversionRate {
    [Currency.ETH]: CurrencyConversionRate[Currency.ETH];
}

export interface ItemData {
    item: FilteredItemsSingleItem['item'];
    owner: FilteredItemsSingleItem['owner'];
    currencyConversionRate: OnlyAcceptedConversionRate;
    lastSale?: number;
}

export function useItemData() {
    const groupId = useGroupId();

    // TODO: This should be using `useNftFilter()
    const filters = useGroupFilterParams();

    // TODO: Replace this hook with a call to `filters.total`.
    // Currently, that property will not filter out "default values",
    // so that will have to be done manually.
    const filtersTotal = useGroupFilterCount();

    const { data, isLoading, hasNextPage, isFetching, isPreviousData, fetchNextPage, total, error } = useFilteredItems({
        groupId,
        filters,
    });

    const { data: conversionData } = useGetCurrencyConversion({ interval: false });

    const conversionValue = conversionData?.ethereum?.usd || 0;
    const currencyConversionRate = {
        [Currency.ETH]: {
            [Currency.USD]: conversionValue,
        },
    };

    // Prevents multiple and/or nested maps to render the items.
    const totalFetchedItems = (data?.pages.length || 0) * GET_GROUP_ITEMS_LIMIT;

    const emptyItems: undefined[] = Array.from(Array(totalFetchedItems));

    const items = emptyItems.reduce<ItemData[]>((collection, _current, index) => {
        const page = Math.floor(index / GET_GROUP_ITEMS_LIMIT);
        const itemPageIndex = index % GET_GROUP_ITEMS_LIMIT;
        const itemData = data?.pages[page].data[itemPageIndex];

        if (!itemData) {
            return collection;
        }

        const lastSale = itemData.item?.item_type.Nft.purchase_date
            ? new Date(itemData.item?.item_type.Nft.purchase_date).getTime()
            : undefined;

        const currentItem: ItemData = {
            item: itemData.item,
            owner: itemData.owner,
            lastSale: lastSale,
            // Should this be defined per-item?
            // Or for the entire grid?
            currencyConversionRate,
        };

        return [...collection, currentItem];
    }, []);

    return {
        items,
        total,
        filtersTotal,
        error,
        loading: isLoading,
        fetching: isFetching,
        hasNextPage,
        isPreviousData,
        fetchNextPage,
    };
}
